<script setup>
/* eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text */
import FinanceiroTabState from './FinanceiroTabState.vue';

defineProps({
  loading: { type: Boolean, default: false },
  error: { type: String, default: '' },
  historico: { type: Object, default: () => ({}) },
  historyFilters: { type: Object, default: () => ({}) },
  historyCardCopy: { type: Object, required: true },
  isPeriodClosed: { type: Boolean, default: false },
  periodClosedCopy: { type: String, default: '' },
  formatBRL: { type: Function, required: true },
  formatDateOnly: { type: Function, required: true },
  formatDateTime: { type: Function, required: true },
  statusMeta: { type: Function, required: true },
  historyKindMeta: { type: Function, required: true },
});

const emit = defineEmits([
  'applyFilters',
  'clearFilters',
  'exportCsv',
  'filterChange',
  'openClosePeriod',
  'retry',
]);

const updateFilter = (key, event) => {
  emit('filterChange', { key, value: event.target.value });
};

const updateLimit = event => {
  emit('filterChange', { key: 'limit', value: Number(event.target.value) });
};
</script>

<template>
  <FinanceiroTabState
    :loading="loading"
    :error="error"
    loading-label="Carregando histórico financeiro..."
    @retry="$emit('retry')"
  >
    <div class="grid w-full gap-6">
      <article
        class="rounded-lais border border-[#e5e7eb] bg-white p-5 shadow-lais-card"
      >
        <div
          class="mb-4 flex flex-wrap items-center justify-between gap-3 border-b border-[#f1f5f9] pb-4"
        >
          <div>
            <h2
              class="font-redHatDisplay text-[24px] font-semibold text-[#1a1a1a]"
            >
              Filtros do histórico
            </h2>
            <p class="mt-1 text-[13px] text-[#718096]">
              Refine transações, histórico público e atividade interna por
              período, tipo e busca livre.
            </p>
          </div>
          <button
            type="button"
            class="text-[13px] font-semibold text-[#49317a]"
            @click="$emit('exportCsv')"
          >
            Exportar histórico público
          </button>
        </div>

        <div class="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          <label class="grid gap-1.5 text-[12px] text-[#718096]">
            Início
            <input
              :value="historyFilters.start_date"
              class="rounded-lais border border-[#e5e7eb] px-3 py-2 text-[13px] text-[#1a1a1a]"
              type="date"
              @input="updateFilter('start_date', $event)"
            />
          </label>
          <label class="grid gap-1.5 text-[12px] text-[#718096]">
            Fim
            <input
              :value="historyFilters.end_date"
              class="rounded-lais border border-[#e5e7eb] px-3 py-2 text-[13px] text-[#1a1a1a]"
              type="date"
              @input="updateFilter('end_date', $event)"
            />
          </label>
          <label class="grid gap-1.5 text-[12px] text-[#718096]">
            Buscar
            <input
              :value="historyFilters.q"
              class="rounded-lais border border-[#e5e7eb] px-3 py-2 text-[13px] text-[#1a1a1a]"
              type="search"
              placeholder="Ticket, visita, status, CRM..."
              @input="updateFilter('q', $event)"
            />
          </label>
          <label class="grid gap-1.5 text-[12px] text-[#718096]">
            Tipo de transação
            <select
              :value="historyFilters.transaction_kind"
              class="rounded-lais border border-[#e5e7eb] px-3 py-2 text-[13px] text-[#1a1a1a]"
              @change="updateFilter('transaction_kind', $event)"
            >
              <option value="">Todas</option>
              <option value="receita">Receita</option>
              <option value="despesa">Despesa</option>
              <option value="comissao">Comissão</option>
            </select>
          </label>
          <label class="grid gap-1.5 text-[12px] text-[#718096]">
            Tipo público
            <select
              :value="historyFilters.public_kind"
              class="rounded-lais border border-[#e5e7eb] px-3 py-2 text-[13px] text-[#1a1a1a]"
              @change="updateFilter('public_kind', $event)"
            >
              <option value="">Todos</option>
              <option value="ticket">Tickets</option>
              <option value="feedback">Feedbacks</option>
              <option value="responde">Responde</option>
              <option value="visita">Visitas</option>
              <option value="status">Status</option>
            </select>
          </label>
          <label class="grid gap-1.5 text-[12px] text-[#718096]">
            Limite
            <select
              :value="historyFilters.limit"
              class="rounded-lais border border-[#e5e7eb] px-3 py-2 text-[13px] text-[#1a1a1a]"
              @change="updateLimit"
            >
              <option :value="20">20</option>
              <option :value="35">35</option>
              <option :value="50">50</option>
            </select>
          </label>
        </div>

        <div class="mt-4 flex flex-wrap gap-3">
          <button
            type="button"
            class="inline-flex items-center justify-center rounded-lais bg-[#49317a] px-4 py-2 text-[13px] font-semibold text-white transition hover:bg-[#3d2966]"
            @click="$emit('applyFilters')"
          >
            Aplicar filtros
          </button>
          <button
            type="button"
            class="inline-flex items-center justify-center rounded-lais border border-[#d6dce5] px-4 py-2 text-[13px] font-semibold text-[#4a5568] transition hover:bg-[#f8fafc]"
            @click="$emit('clearFilters')"
          >
            Limpar
          </button>
        </div>
      </article>

      <div class="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <article
          v-for="(label, key) in historyCardCopy"
          :key="key"
          class="rounded-lais border border-[#e5e7eb] bg-white p-5 shadow-lais-card"
        >
          <p
            class="text-[12px] font-semibold uppercase tracking-wide text-[#a0aec0]"
          >
            {{ label }}
          </p>
          <strong
            class="font-redHatDisplay mt-3 block text-[28px] leading-none text-[#1a1a1a]"
          >
            {{ formatBRL(historico.cards?.[key]) }}
          </strong>
          <p class="mt-2 text-[12px] text-[#718096]">
            {{ historico.periodo?.label || '-' }}
          </p>
        </article>
      </div>

      <article
        class="rounded-lais border border-[#e5e7eb] bg-white p-5 shadow-lais-card"
      >
        <div
          class="mb-4 flex flex-wrap items-center justify-between gap-3 border-b border-[#f1f5f9] pb-4"
        >
          <div>
            <h2
              class="font-redHatDisplay text-[24px] font-semibold text-[#1a1a1a]"
            >
              Fechamento por mês
            </h2>
            <p class="mt-1 text-[13px] text-[#718096]">
              Panorama consolidado do saldo, receitas, despesas e comissões.
            </p>
          </div>
          <div class="flex flex-wrap items-center justify-end gap-2">
            <span
              class="rounded-full bg-[#f6f2fb] px-3 py-1 text-[12px] font-semibold text-[#49317a]"
            >
              {{ historico.periodo?.label || '-' }}
            </span>
            <span
              v-if="isPeriodClosed"
              class="inline-flex items-center gap-2 rounded-full bg-emerald-50 px-3 py-1 text-[12px] font-semibold text-emerald-700"
              :title="periodClosedCopy"
            >
              <span class="i-lucide-lock size-3.5" aria-hidden="true" />
              Período fechado
            </span>
            <button
              v-else
              type="button"
              class="inline-flex h-9 items-center gap-2 rounded-lais border border-[#49317a] px-3 text-[12px] font-semibold text-[#49317a] transition-colors hover:bg-[#f8f4fc]"
              @click="$emit('openClosePeriod')"
            >
              <span class="i-lucide-lock-keyhole size-4" aria-hidden="true" />
              Fechar período
            </button>
          </div>
        </div>

        <div
          v-if="isPeriodClosed"
          class="mb-4 rounded-lais border border-emerald-100 bg-emerald-50 px-4 py-3 text-[13px] text-emerald-800"
        >
          {{ periodClosedCopy }}
          Novas movimentações continuam visíveis, mas o fechamento deste
          intervalo fica registrado na conta.
        </div>

        <div
          v-if="historico.serie_mensal?.length"
          class="grid gap-3 md:grid-cols-2 xl:grid-cols-3"
        >
          <div
            v-for="item in historico.serie_mensal"
            :key="item.key"
            class="rounded-lais border border-[#eef1f6] bg-[#faf8fd] p-4"
          >
            <div class="mb-3 flex items-center justify-between gap-3">
              <strong class="font-redHatDisplay text-[18px] text-[#1a1a1a]">
                {{ item.label }}
              </strong>
              <span class="text-[12px] font-semibold text-[#49317a]">
                Saldo {{ formatBRL(item.saldo) }}
              </span>
            </div>
            <div class="grid gap-2 text-[13px] text-[#4a5568]">
              <div class="flex items-center justify-between gap-3">
                <span>Receita</span>
                <strong>{{ formatBRL(item.receita) }}</strong>
              </div>
              <div class="flex items-center justify-between gap-3">
                <span>Despesa</span>
                <strong>{{ formatBRL(item.despesa) }}</strong>
              </div>
              <div class="flex items-center justify-between gap-3">
                <span>Comissão</span>
                <strong>{{ formatBRL(item.comissao) }}</strong>
              </div>
            </div>
          </div>
        </div>
        <div
          v-else
          class="rounded-lais border border-dashed border-[#e2e8f0] p-6 text-[13px] text-[#718096]"
        >
          Ainda não há movimentações suficientes para montar a série mensal.
        </div>
      </article>

      <div class="grid gap-6 xl:grid-cols-[minmax(0,1.15fr)_minmax(0,0.85fr)]">
        <article
          class="rounded-lais border border-[#e5e7eb] bg-white p-5 shadow-lais-card"
        >
          <div
            class="mb-4 flex flex-wrap items-center justify-between gap-3 border-b border-[#f1f5f9] pb-4"
          >
            <div>
              <h2
                class="font-redHatDisplay text-[24px] font-semibold text-[#1a1a1a]"
              >
                Transações
              </h2>
              <p class="mt-1 text-[13px] text-[#718096]">
                Receita, despesas e comissões consolidadas no mesmo fluxo.
              </p>
            </div>
            <span class="text-[12px] font-semibold text-[#a0aec0]">
              {{ historico.transacoes?.length || 0 }} registros
            </span>
          </div>

          <div v-if="historico.transacoes?.length" class="grid gap-3">
            <article
              v-for="item in historico.transacoes"
              :key="item.id"
              class="rounded-lais border border-[#eef1f6] p-4"
            >
              <div class="flex flex-wrap items-start justify-between gap-3">
                <div class="min-w-0">
                  <div class="flex flex-wrap items-center gap-2">
                    <span
                      class="inline-flex items-center gap-2 rounded-full px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide"
                      :class="historyKindMeta(item.kind).cls"
                    >
                      <span
                        class="size-3.5"
                        :class="historyKindMeta(item.kind).icon"
                        aria-hidden="true"
                      />
                      {{ historyKindMeta(item.kind).label }}
                    </span>
                    <span
                      class="inline-flex rounded-full px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide"
                      :class="statusMeta(item.status).cls"
                    >
                      {{ statusMeta(item.status).label }}
                    </span>
                  </div>
                  <h3
                    class="font-redHatDisplay mt-3 text-[18px] font-semibold text-[#1a1a1a]"
                  >
                    {{ item.title }}
                  </h3>
                  <p
                    v-if="item.description"
                    class="mt-1 text-[13px] text-[#718096]"
                  >
                    {{ item.description }}
                  </p>
                  <div
                    class="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-[12px] text-[#a0aec0]"
                  >
                    <span>{{
                      item.date_label || formatDateOnly(item.occurred_at)
                    }}</span>
                    <span>{{ item.source || 'manual' }}</span>
                    <span v-if="item.reconciliation_status">
                      Conciliação
                      {{ statusMeta(item.reconciliation_status).label }}
                    </span>
                    <span v-if="item.receipt_checksum">
                      Comprovante
                      {{ String(item.receipt_checksum).slice(0, 12) }}
                    </span>
                  </div>
                </div>
                <div class="text-right">
                  <strong
                    class="block text-[16px] font-semibold text-[#1a1a1a]"
                  >
                    {{ formatBRL(item.amount) }}
                  </strong>
                  <a
                    v-if="item.link_url"
                    :href="item.link_url"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="mt-2 inline-block text-[12px] font-semibold text-[#49317a]"
                  >
                    Abrir comprovante
                  </a>
                </div>
              </div>
            </article>
          </div>
          <div
            v-else
            class="rounded-lais border border-dashed border-[#e2e8f0] p-6 text-[13px] text-[#718096]"
          >
            Nenhuma transação registrada no período.
          </div>
        </article>

        <div class="grid gap-6">
          <article
            class="rounded-lais border border-[#e5e7eb] bg-white p-5 shadow-lais-card"
          >
            <div
              class="mb-4 flex flex-wrap items-center justify-between gap-3 border-b border-[#f1f5f9] pb-4"
            >
              <div>
                <h2
                  class="font-redHatDisplay text-[24px] font-semibold text-[#1a1a1a]"
                >
                  Histórico público
                </h2>
                <p class="mt-1 text-[13px] text-[#718096]">
                  Tudo o que entrou pelas superfícies abertas da Lais.
                </p>
              </div>
              <span class="text-[12px] font-semibold text-[#a0aec0]">
                {{ historico.resumo_publico?.total || 0 }} eventos
              </span>
            </div>

            <div class="grid gap-3 sm:grid-cols-2">
              <div class="rounded-lais border border-[#eef1f6] p-4">
                <span class="text-[12px] text-[#a0aec0]">Tickets</span>
                <strong
                  class="mt-2 block text-[26px] font-semibold text-[#1a1a1a]"
                >
                  {{ historico.resumo_publico?.tickets || 0 }}
                </strong>
              </div>
              <div class="rounded-lais border border-[#eef1f6] p-4">
                <span class="text-[12px] text-[#a0aec0]">Responde</span>
                <strong
                  class="mt-2 block text-[26px] font-semibold text-[#1a1a1a]"
                >
                  {{ historico.resumo_publico?.sessoes_responde || 0 }}
                </strong>
              </div>
              <div class="rounded-lais border border-[#eef1f6] p-4">
                <span class="text-[12px] text-[#a0aec0]">
                  Feedbacks no suporte
                </span>
                <strong
                  class="mt-2 block text-[26px] font-semibold text-[#1a1a1a]"
                >
                  {{ historico.resumo_publico?.feedback_suporte || 0 }}
                </strong>
              </div>
              <div class="rounded-lais border border-[#eef1f6] p-4">
                <span class="text-[12px] text-[#a0aec0]">
                  Visitas públicas
                </span>
                <strong
                  class="mt-2 block text-[26px] font-semibold text-[#1a1a1a]"
                >
                  {{ historico.resumo_publico?.visitas_publicas || 0 }}
                </strong>
              </div>
              <div class="rounded-lais border border-[#eef1f6] p-4">
                <span class="text-[12px] text-[#a0aec0]">
                  Incidentes de status
                </span>
                <strong
                  class="mt-2 block text-[26px] font-semibold text-[#1a1a1a]"
                >
                  {{ historico.resumo_publico?.incidentes_status || 0 }}
                </strong>
              </div>
            </div>

            <div
              v-if="historico.historico_publico?.length"
              class="mt-4 grid gap-3"
            >
              <article
                v-for="item in historico.historico_publico"
                :key="item.id"
                class="rounded-lais border border-[#eef1f6] bg-[#faf8fd] p-4"
              >
                <div class="flex flex-wrap items-start justify-between gap-3">
                  <div class="min-w-0">
                    <div class="flex flex-wrap items-center gap-2">
                      <span
                        class="inline-flex items-center gap-2 rounded-full px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide"
                        :class="historyKindMeta(item.kind).cls"
                      >
                        <span
                          class="size-3.5"
                          :class="historyKindMeta(item.kind).icon"
                          aria-hidden="true"
                        />
                        {{ historyKindMeta(item.kind).label }}
                      </span>
                      <span
                        class="inline-flex rounded-full px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide"
                        :class="statusMeta(item.status).cls"
                      >
                        {{ statusMeta(item.status).label }}
                      </span>
                    </div>
                    <h3
                      class="font-redHatDisplay mt-3 text-[17px] font-semibold text-[#1a1a1a]"
                    >
                      {{ item.title }}
                    </h3>
                    <p
                      v-if="item.description"
                      class="mt-1 text-[13px] text-[#718096]"
                    >
                      {{ item.description }}
                    </p>
                  </div>
                  <div class="text-right text-[12px] text-[#a0aec0]">
                    {{ item.date_label || formatDateTime(item.occurred_at) }}
                  </div>
                </div>
                <a
                  v-if="item.link_url"
                  :href="item.link_url"
                  class="mt-3 inline-flex text-[12px] font-semibold text-[#49317a]"
                >
                  Abrir detalhe
                </a>
              </article>
            </div>
            <div
              v-else
              class="mt-4 rounded-lais border border-dashed border-[#e2e8f0] p-6 text-[13px] text-[#718096]"
            >
              Ainda não houve atividade pública registrada no período.
            </div>
          </article>
        </div>
      </div>

      <article
        class="rounded-lais border border-[#e5e7eb] bg-white p-5 shadow-lais-card"
      >
        <div
          class="mb-4 flex flex-wrap items-center justify-between gap-3 border-b border-[#f1f5f9] pb-4"
        >
          <div>
            <h2
              class="font-redHatDisplay text-[24px] font-semibold text-[#1a1a1a]"
            >
              Atividade interna
            </h2>
            <p class="mt-1 text-[13px] text-[#718096]">
              Log consolidado de operações, conversas e movimentações
              relevantes.
            </p>
          </div>
          <span v-if="loading" class="text-[12px] font-semibold text-[#a0aec0]">
            Atualizando...
          </span>
        </div>

        <div v-if="historico.atividades?.length" class="grid gap-3">
          <article
            v-for="item in historico.atividades"
            :key="item.id"
            class="rounded-lais border border-[#eef1f6] p-4"
          >
            <div class="flex flex-wrap items-start justify-between gap-3">
              <div class="min-w-0">
                <h3
                  class="font-redHatDisplay text-[17px] font-semibold text-[#1a1a1a]"
                >
                  {{ item.action }}
                </h3>
                <p class="mt-1 text-[13px] text-[#718096]">
                  {{ item.description }}
                </p>
              </div>
              <div class="text-right">
                <strong class="block text-[13px] text-[#49317a]">
                  {{ item.actor || 'Sistema' }}
                </strong>
                <span class="mt-1 block text-[12px] text-[#a0aec0]">
                  {{ item.date_label || formatDateTime(item.occurred_at) }}
                </span>
              </div>
            </div>
          </article>
        </div>
        <div
          v-else
          class="rounded-lais border border-dashed border-[#e2e8f0] p-6 text-[13px] text-[#718096]"
        >
          Nenhuma atividade interna registrada no período.
        </div>
      </article>
    </div>
  </FinanceiroTabState>
</template>
